import type { SPRow, SPRowComponent } from '@/api/hooks/useSupplyPlans'
import { RoutingCell } from '@/components/Display/RoutingCell'
import { integrationStatusConfig } from '@/pages/Dashboard/panels/OrdersPanel/OrdersCellRenderers'
import { formatEffectiveDate, formatTimestamp } from '@/utils/dateFormatters'
import { ApiOutlined, PartitionOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton } from '@gravitate-js/excalibrr'
import { IssueCategoryCell } from '@pages/Dashboard/panels/ConnectionLogsPanel/components/IssueCategoryCell'
import {
  issueCategoryFilterLabel,
  issueCategoryLabel,
} from '@pages/Dashboard/panels/ConnectionLogsPanel/components/LogDetailsPanel/categories'
import type {
  ColDef,
  ICellRendererParams,
  SetFilterValuesFuncParams,
} from 'ag-grid-community'
import { Tooltip } from 'antd'
import { useLinkClickHandler } from 'react-router-dom'

function AuditButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip title='View Translation Audit'>
      <GraviButton
        size='middle'
        appearance='text'
        icon={
          <PartitionOutlined
            style={{ color: 'hsl(var(--primary))', fontSize: 18 }}
          />
        }
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
      />
    </Tooltip>
  )
}

function BatchLinkButton({ batchId }: { batchId: string }) {
  const url = `/integration-management/supply-plan-logs?batch_id=${encodeURIComponent(batchId)}`
  const handleLinkClick = useLinkClickHandler<HTMLElement>(url)
  return (
    <Tooltip title='View Integration Logs'>
      <GraviButton
        size='middle'
        appearance='text'
        href={url}
        icon={
          <ApiOutlined style={{ color: 'hsl(var(--primary))', fontSize: 18 }} />
        }
        onClick={(e) => {
          e.stopPropagation()
          handleLinkClick(e)
        }}
      />
    </Tooltip>
  )
}

export type GetFilterOptions = (colId: string) => Promise<(string | null)[]>

export const UNKNOWN_GROUP_LABEL = '(Unknown)'

const PENDING_STATUSES = new Set([
  'origin_mapped',
  'master_mapped',
  'destination_mapped',
])

function statusDisplay(status: string) {
  if (PENDING_STATUSES.has(status)) {
    return { color: 'default', label: 'Pending', icon: null }
  }
  return (
    integrationStatusConfig[status] ?? {
      color: 'default',
      label: status,
      icon: null,
    }
  )
}

interface SupplyPlansColumnDefsOptions {
  tenantName: string
  onAuditClick?: (row: SPRow) => void
}

function componentSideColumnDefs(
  colPrefix: string,
  getComponent: (row: SPRow) => SPRowComponent | null | undefined,
  setFilterParams: (
    colId: string,
  ) => Pick<ColDef<SPRow>, 'filter' | 'filterParams'>,
  hide: boolean = false,
): ColDef<SPRow>[] {
  const siteColId = `${colPrefix}site`
  const productColId = `${colPrefix}product`
  const terminalColId = `${colPrefix}component_terminal`
  const supplierColId = `${colPrefix}component_supplier`
  return [
    {
      headerName: `Site`,
      colId: siteColId,
      field: siteColId as ColDef<SPRow>['field'],
      minWidth: 100,
      hide,
      ...setFilterParams(siteColId),
      valueGetter: (params) => {
        if (!params.data) return undefined
        const leafValue = getComponent(params.data)?.site
        if (leafValue !== undefined) return leafValue
        if (!params.node?.group) return undefined
        return (
          (params.data as unknown as Record<string, unknown>)[siteColId] ?? ''
        )
      },
    },
    {
      headerName: `Product`,
      colId: productColId,
      field: productColId as ColDef<SPRow>['field'],
      minWidth: 160,
      hide,
      ...setFilterParams(productColId),
      // Same group-only fallback chain as Site above.
      valueGetter: (params) => {
        if (!params.data) return undefined
        const leafValue = getComponent(params.data)?.product
        if (leafValue !== undefined) return leafValue
        if (!params.node?.group) return undefined
        return (
          (params.data as unknown as Record<string, unknown>)[productColId] ??
          ''
        )
      },
    },
    {
      // Raw source/goid id backing the Product column above — not
      // separately filterable, kept hidden for support/debugging lookups.
      headerName: `Product Id`,
      colId: `${colPrefix}product_id`,
      minWidth: 140,
      hide: true,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.product_id ?? undefined)
          : undefined,
    },
    {
      headerName: `Load Product`,
      colId: `${colPrefix}component_product`,
      minWidth: 160,
      hide,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_product ?? undefined)
          : undefined,
    },
    {
      headerName: `Load Product Id`,
      colId: `${colPrefix}component_product_id`,
      minWidth: 140,
      hide: true,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_product_id ?? undefined)
          : undefined,
    },
    {
      headerName: `Terminal`,
      colId: terminalColId,
      minWidth: 160,
      hide,
      ...setFilterParams(terminalColId),
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_terminal ?? undefined)
          : undefined,
    },
    {
      headerName: `Terminal Id`,
      colId: `${colPrefix}component_terminal_id`,
      minWidth: 140,
      hide: true,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_terminal_id ?? undefined)
          : undefined,
    },
    {
      headerName: `Supplier`,
      colId: supplierColId,
      minWidth: 160,
      hide,
      ...setFilterParams(supplierColId),
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_supplier ?? undefined)
          : undefined,
    },
    {
      headerName: `Supplier Id`,
      colId: `${colPrefix}component_supplier_id`,
      minWidth: 140,
      hide: true,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.component_supplier_id ?? undefined)
          : undefined,
    },
    {
      headerName: `Curve Id`,
      colId: `${colPrefix}curve_id`,
      minWidth: 120,
      hide: true,
      filter: false,
      valueGetter: (params) =>
        params.data
          ? (getComponent(params.data)?.curve_id ?? undefined)
          : undefined,
    },
  ]
}

export function createSupplyPlansColumnDefs({
  tenantName,
  onAuditClick,
}: SupplyPlansColumnDefsOptions): ColDef<SPRow>[] {
  function setFilterParams(
    colId: string,
    valueFormatter?: (value: string) => string,
  ): Pick<ColDef<SPRow>, 'filter' | 'filterParams'> {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: SetFilterValuesFuncParams<SPRow>) => {
          const getFilterOptions = (
            params.context as { getFilterOptions: GetFilterOptions }
          ).getFilterOptions
          getFilterOptions(colId)
            .then((values) => params.success(values))
            .catch(() => params.success([]))
        },
        ...(valueFormatter && {
          valueFormatter: (p: { value: string | null }) =>
            p.value ? valueFormatter(p.value) : '(Blanks)',
        }),
        // Re-ask on every open. The values depend on the selected tenant and
        // the As Of date, so a list built once at first open goes stale the
        // moment either changes; getFilterOptions caches per tenant+filters,
        // so reopening without changing them costs nothing.
        refreshValuesOnOpen: true,
      },
    }
  }

  const columnDefs: ColDef<SPRow>[] = [
    {
      headerName: '',
      colId: 'batch_link',
      pinned: 'left',
      width: 56,
      initialFlex: 0,
      maxWidth: 56,
      minWidth: 56,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<SPRow>) => {
        if (!params.data?.batch_id) return null
        return <BatchLinkButton batchId={params.data.batch_id} />
      },
    },
    {
      headerName: '',
      colId: 'audit_link',
      pinned: 'left',
      width: 56,
      initialFlex: 0,
      maxWidth: 56,
      minWidth: 56,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<SPRow>) => {
        if (!params.data || !onAuditClick) return null
        const row = params.data
        return <AuditButton onClick={() => onAuditClick(row)} />
      },
    },
    {
      headerName: 'Partner',
      colId: 'partner_tenant',
      field: 'partner',
      width: 160,
      initialFlex: 0,
      ...setFilterParams('partner_tenant'),
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 130,
      initialFlex: 0,
      ...setFilterParams('status', (value) => statusDisplay(value).label),
      cellRenderer: (params: ICellRendererParams<SPRow>) => {
        const status = params.data?.status
        if (!status) return null
        const config = statusDisplay(status)
        return (
          <BBDTag color={config.color}>
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {config.icon} {config.label}
            </span>
          </BBDTag>
        )
      },
    },
    {
      headerName: 'Issue Type',
      colId: 'issue_type',
      width: 200,
      initialFlex: 0,
      ...setFilterParams('issue_type', issueCategoryFilterLabel),
      valueGetter: (params) => issueCategoryLabel(params.data?.log),
      cellRenderer: (params: ICellRendererParams<SPRow>) => (
        <IssueCategoryCell log={params.data?.log} />
      ),
    },
    {
      headerName: 'Rank',
      field: 'rank',
      width: 70,
      initialFlex: 0,
      filter: false,
    },
    {
      headerName: 'Load Number',
      field: 'load_number',
      width: 140,
      initialFlex: 0,
      filter: false,
    },
    {
      headerName: tenantName,
      children: [
        ...componentSideColumnDefs(
          '',
          (row) => row.internal_component,
          setFilterParams,
        ),
      ],
    },
    {
      headerName: 'Partner',
      children: [
        ...componentSideColumnDefs(
          'partner_',
          (row) => row.partner_component,
          setFilterParams,
        ),
      ],
    },
    {
      headerName: 'Effective From',
      field: 'effective_from',
      width: 130,
      initialFlex: 0,
      filter: false,
      valueFormatter: (params) => formatEffectiveDate(params.value),
    },
    {
      headerName: 'Effective To',
      field: 'effective_to',
      width: 130,
      initialFlex: 0,
      filter: false,
      valueFormatter: (params) => formatEffectiveDate(params.value),
    },
    {
      headerName: 'Price Type',
      field: 'price_type',
      width: 120,
      initialFlex: 0,
      ...setFilterParams('price_type'),
    },
    {
      headerName: 'Contract',
      field: 'contract',
      width: 120,
      initialFlex: 0,
      hide: true,
      filter: false,
    },
    {
      headerName: 'Load Index',
      field: 'component_idx',
      width: 90,
      initialFlex: 0,
      hide: true,
      filter: false,
    },
    {
      headerName: 'Master',
      children: [
        ...componentSideColumnDefs(
          'master_',
          (row) => row.master_component,
          setFilterParams,
          true,
        ),
      ],
      hide: true,
    },
    {
      headerName: 'Updated On',
      field: 'updated_on',
      width: 190,
      initialFlex: 0,
      hide: true,
      filter: false,
      valueFormatter: (params) => formatTimestamp(params.value),
    },
    {
      headerName: 'Routing',
      colId: 'routing',
      width: 180,
      initialFlex: 0,
      hide: true,
      filter: false,
      valueGetter: (params) => {
        const routing = params.data?.log?.routing
        return routing
          ? `${routing.origin_tenant_name} → ${routing.destination_tenant_name}`
          : null
      },
      cellRenderer: (params: ICellRendererParams<SPRow>) => (
        <RoutingCell routing={params.data?.log?.routing} />
      ),
    },
    {
      headerName: 'Batch',
      field: 'batch_id',
      hide: true,
      width: 160,
      initialFlex: 0,
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['equals', 'contains'] },
    },
  ]

  const groupableColIds = new Set([
    'site',
    'product',
    'partner_site',
    'partner_product',
    'master_site',
    'master_product',
  ])
  return columnDefs.map((col) => ({
    ...col,
    enableRowGroup: col.colId ? groupableColIds.has(col.colId) : false,
  }))
}
